import api from './client';

const PREFIX = 'agence_';

// Two concurrent callers that miss the same key (e.g. Dashboard's unawaited getTransactions()
// followed by a fast nav to Expenses before it resolves) used to each issue a full duplicate
// fetch — neither had started, so neither saw the other's in-flight request. Track the pending
// promise per key and hand it to a second concurrent miss instead of re-fetching.
const _inFlight = new Map();

/**
 * Fetch with sessionStorage cache.
 * @param {string} key        - cache key (no prefix needed)
 * @param {string} endpoint   - API path passed to api.get()
 * @param {number} ttlMs      - cache lifetime in ms
 * @param {Function} extract  - fn(data) => value to store (defaults to full data)
 */
export async function getCached(key, endpoint, ttlMs, extract = d => d) {
  const storageKey = PREFIX + key;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      const { value, ts } = JSON.parse(raw);
      if (Date.now() - ts < ttlMs) return value;
    }
  } catch {
    // corrupted — fall through
  }

  const pending = _inFlight.get(storageKey);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const { data } = await api.get(endpoint);
      const value = extract(data);
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ value, ts: Date.now() }));
      } catch {
        // storage full — no-op
      }
      return value;
    } finally {
      _inFlight.delete(storageKey);
    }
  })();
  _inFlight.set(storageKey, promise);
  return promise;
}

export function invalidate(key) {
  sessionStorage.removeItem(PREFIX + key);
}

export function getCachedSync(key, ttlMs = Infinity) {
  const storageKey = PREFIX + key;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      const { value, ts } = JSON.parse(raw);
      if (Date.now() - ts < ttlMs) return value;
    }
  } catch {}
  return null;
}

export function setCached(key, value) {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify({ value, ts: Date.now() }));
  } catch {
    // storage full — no-op
  }
}

export function invalidateAll() {
  Object.keys(sessionStorage)
    .filter(k => k.startsWith(PREFIX))
    .forEach(k => sessionStorage.removeItem(k));
}

// Convenience wrappers with pre-set TTLs
const MIN = 60 * 1000;

export const getPortfolio   = () => getCached('portfolio',    '/portfolio',    2 * MIN, d => d);
export const getAccounts    = () => getCached('accounts',     '/accounts',     5 * MIN, d => d.accounts || []);
export const getGoals       = () => getCached('goals',        '/goals',        5 * MIN, d => d.goals || []);
export const getTransactions = () => getCached('transactions', '/transactions', 5 * MIN, d => d.transactions || []);
export const getWatchlist   = () => getCached('watchlist',    '/watchlist',    2 * MIN, d => d.watchlist || []);
export const getHousehold   = () => getCached('household',    '/household',    5 * MIN, d => d.household);
export const getProfile     = () => getCached('profile',      '/auth/me',      5 * MIN, d => d);
export const getTradeHistory    = () => getCached('trades',          '/trades',          2 * MIN, d => d.trades || []);
export const getAlpacaAccounts  = () => getCached('alpacaAccounts',  '/alpaca/accounts', 2 * MIN, d => d.accounts || []);
export const getBudgets         = () => getCached('budgets',         '/budgets',         2 * MIN, d => d.budgets || []);
export const getMerchantBudgets = () =>
  getCached('merchantBudgets', '/merchant-budgets', 2 * MIN,
    d => ({ budgets: d.budgets || [], other: { spent: d.other?.spent || 0, merchants: d.other?.merchants || [] } }));
export const getMerchantBudgetCandidates = () =>
  getCached('merchantBudgetCandidates', '/merchant-budgets/candidates', 5 * MIN,
    d => ({ recentMerchants: d.recentMerchants || [], recurringMerchants: d.recurringMerchants || [] }));
export const getAutomations     = () => getCached('automations',      '/automations',     1 * MIN, d => d.automations || []);
export const getProfitability      = (accountIds, months = 6, multi = false) => {
  const param = multi ? `account_ids=${accountIds || ''}` : `account_id=${accountIds || ''}`;
  return getCached(`profitability_${accountIds}_${months}`, `/profitability?${param}&months=${months}`, 5 * MIN, d => d.months || []);
};
export const getInsightPreferences = () =>
  getCached('insightPrefs', '/insights/preferences', 5 * MIN, d => d.preferences || {});
export function invalidateInsightPrefs() { invalidate('insightPrefs'); }

// Per-account portfolio/trades (keyed by accountId so switching accounts uses separate slots)
export const getPortfolioForAccount = (accountId) =>
  getCached(`portfolio_${accountId}`, `/portfolio?account_id=${accountId}`, 2 * MIN, d => d);
export const getTradesForAccount = (accountId) =>
  getCached(`trades_${accountId}`, `/trades?account_id=${accountId}`, 2 * MIN, d => d.trades || []);
export function invalidatePortfolioForAccount(accountId) { invalidate(`portfolio_${accountId}`); }
export function invalidateTradesForAccount(accountId) { invalidate(`trades_${accountId}`); }

export const getPlaidHoldings = () =>
  getCached('plaidHoldings', '/accounts/plaid-holdings', 5 * MIN, d => d);
export function invalidatePlaidHoldings() { invalidate('plaidHoldings'); }

export const getNotifPreferences = () =>
  getCached('notifPreferences', '/notifications/preferences', 5 * MIN, d => d);
export function invalidateNotifPreferences() { invalidate('notifPreferences'); }

// Per-ticker caching: batch-fetch uncached tickers in one request, cache individually.
// Adding one ticker only fetches that ticker; existing tickers served from cache.
export async function getNews(tickers) {
  if (!tickers.length) return [];
  const now = Date.now();
  const TTL = 2 * MIN;
  const cached = {};
  const missing = [];

  for (const t of tickers) {
    const raw = sessionStorage.getItem(PREFIX + 'news_' + t);
    if (raw) {
      try {
        const { value, ts } = JSON.parse(raw);
        if (now - ts < TTL) { cached[t] = value; continue; }
      } catch { /* corrupted — re-fetch */ }
    }
    missing.push(t);
  }

  if (missing.length > 0) {
    const { data } = await api.get(`/news?tickers=${missing.join(',')}`);
    for (const item of (data.news || [])) {
      try {
        sessionStorage.setItem(PREFIX + 'news_' + item.ticker, JSON.stringify({ value: item, ts: now }));
      } catch { /* storage full */ }
      cached[item.ticker] = item;
    }
  }

  return tickers.map(t => cached[t]).filter(Boolean);
}

export function invalidateAllNews() {
  Object.keys(sessionStorage)
    .filter(k => k.startsWith(PREFIX + 'news_'))
    .forEach(k => sessionStorage.removeItem(k));
}

export async function dismissInsight(fingerprint, permanent = true) {
  await api.post('/insights/dismiss', { fingerprint, permanent });
}
